import { DEFAULT_TIMEZONE, LOCIZE_NS } from '@avanta/shared/constants/common';
import { useDialogContext } from '@avanta/shared/contexts/Dialog';
import { useMutation, useQueryClient, type UseMutationResult } from '@tanstack/react-query';
import { type AxiosError, type AxiosResponse } from 'axios';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { MUTATION_KEYS, QUERY_KEYS } from 'constants/common';
import { CREATE_EMPLOYEE_STATUS } from 'constants/employees';
import { EmployeeService } from 'services/employees';
import { type CreateEmployeesResult } from 'types/employees';
import { generateSecurePassword } from 'utils/common';
import { parseEmployeesCsv } from './util';

export type CreateEmployeesFromCsvVariables = {
  file: File;
  enterpriseId: string;
  departmentId?: string | null;
  sendEmail?: boolean;
};

export const useCreateEmployees = (): UseMutationResult<
  AxiosResponse<CreateEmployeesResult[]>,
  AxiosError,
  CreateEmployeesFromCsvVariables
> => {
  const queryClient = useQueryClient();
  const { hideDialog } = useDialogContext();
  const { t } = useTranslation(LOCIZE_NS.INSURANCE);
  return useMutation({
    mutationKey: [MUTATION_KEYS.CREATE_EMPLOYEES],
    mutationFn: async ({
      file,
      enterpriseId,
      departmentId,
      sendEmail,
    }: CreateEmployeesFromCsvVariables) => {
      const employees = parseEmployeesCsv(await file.text(), {
        enterpriseId,
        timeZone: DEFAULT_TIMEZONE,
        fallbackDepartmentId: departmentId ?? undefined,
        generatePassword: generateSecurePassword,
      });

      return EmployeeService.createEmployees({
        enterpriseId,
        departmentId,
        sendEmail,
        employees,
      });
    },
    onSuccess: async response => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.FETCH_INSURED_USERS] }),
        queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.FETCH_INSURED_USERS_COUNT] }),
      ]);

      if (response.data.some(result => result.status !== CREATE_EMPLOYEE_STATUS.CREATED)) {
        return;
      }
      toast.success(t('InsuredPersonsImport.create.success'));
      hideDialog();
    },
    onError: () => {
      toast.error(t('InsuredPersonsImport.create.error'));
    },
  });
};
